import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  alpha,
  useTheme,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { format, isToday, isTomorrow } from "date-fns";
import { toast } from "react-toastify";
import { LABELS } from "../../platform/config";
import {
  ClientAttachment,
  ClientNote,
  ClientRecord,
  ConsentForm,
  SignedConsent,
  Visit,
  addClientNote,
  deleteClientAttachment,
  deleteClientNote,
  getClientRecord,
  listConsentForms,
  revokeConsent,
  saveMedicalInfo,
  updateClientNote,
} from "../../services/clientRecordsService";
import MedicalInfoFields, { MedicalInfoValues, toMedicalValues } from "./MedicalInfoFields";
import ConsentSignPanel from "./ConsentSignPanel";
import UploadFileDialog from "./UploadFileDialog";
import AttachmentViewer from "./AttachmentViewer";
import { FileTypeIcon, fileSize } from "./fileDisplay";
import ConfirmDialog from "../ConfirmDialog";

const errorText = (err: any, fallback: string) => {
  const message = err?.response?.data?.message;
  return (Array.isArray(message) ? message[0] : message) || fallback;
};

const STATUS_STYLE: Record<Visit["status"], { label: string; color: "default" | "success" | "warning" | "error" | "info" }> = {
  PENDING: { label: "Booked", color: "info" },
  IN_PROGRESS: { label: "In progress", color: "warning" },
  DONE: { label: "Done", color: "success" },
  CANCELLED: { label: "Cancelled", color: "error" },
  REFUNDED: { label: "Refunded", color: "default" },
};

const visitDay = (iso: string) => {
  const date = new Date(iso);
  if (isToday(date)) return `Today, ${format(date, "HH:mm")}`;
  if (isTomorrow(date)) return `Tomorrow, ${format(date, "HH:mm")}`;
  return format(date, "EEE d MMM yyyy, HH:mm");
};

// Past visits listed before "Show more".
const HISTORY_PAGE = 5;

const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  action,
  children,
}) => (
  <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, p: { xs: 1.5, sm: 2 } }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, minHeight: 32 }}>
      <Typography component="h3" sx={{ fontWeight: 700, fontSize: "1rem", flexGrow: 1 }}>
        {title}
      </Typography>
      {action}
    </Box>
    {children}
  </Paper>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: "0.88rem", color: "text.secondary" }}>{children}</Typography>
);

/**
 * The client's file ("Patient file" in the clinic build): medical / health
 * info on top so allergies are never missed, then visits with a note each,
 * consent forms, files and general notes. Same component in both builds.
 */
const ClientFile: React.FC<{ clientId: string }> = ({ clientId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [record, setRecord] = useState<ClientRecord | null>(null);
  const [forms, setForms] = useState<ConsentForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editingMedical, setEditingMedical] = useState(false);
  const [medicalDraft, setMedicalDraft] = useState<MedicalInfoValues>(toMedicalValues(null));
  const [savingMedical, setSavingMedical] = useState(false);

  const [noteFor, setNoteFor] = useState<string | null>(null); // appointment id, or "general"
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editingNote, setEditingNote] = useState<ClientNote | null>(null);

  const [signing, setSigning] = useState<ConsentForm | null>(null);
  const [viewingConsent, setViewingConsent] = useState<SignedConsent | null>(null);

  // The upload window, opened for the file in general or for one visit.
  const [uploadFor, setUploadFor] = useState<{ visitId?: string } | null>(null);
  // Which files the viewer steps through, and the one on screen.
  const [viewer, setViewer] = useState<{ files: ClientAttachment[]; index: number | null }>({ files: [], index: null });
  const [fileFilter, setFileFilter] = useState<string | null>(null);

  // null = pick for the owner: upcoming when something is booked.
  const [visitTab, setVisitTab] = useState<"upcoming" | "past" | null>(null);
  const [openVisit, setOpenVisit] = useState<string | null>(null);
  const [historyShown, setHistoryShown] = useState(HISTORY_PAGE);
  // The "are you sure?" window for deleting and withdrawing.
  const [confirming, setConfirming] = useState<{
    title: string;
    message: string;
    confirmText: string;
    run: () => Promise<void>;
  } | null>(null);

  const load = useCallback(async () => {
    try {
      const [nextRecord, nextForms] = await Promise.all([getClientRecord(clientId), listConsentForms()]);
      setRecord(nextRecord);
      setForms(nextForms);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorText(err, `Could not load the ${LABELS.clientFile.toLowerCase()}.`));
    }
    setLoading(false);
  }, [clientId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const notesByVisit = useMemo(() => {
    const map = new Map<string, ClientNote[]>();
    for (const note of record?.notes ?? []) {
      const key = note.appointmentId ?? "general";
      map.set(key, [...(map.get(key) ?? []), note]);
    }
    return map;
  }, [record]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (loadError || !record) return <Alert severity="error">{loadError}</Alert>;

  const info = record.medicalInfo;
  const hasAllergies = (info?.allergies.length ?? 0) > 0;

  // ----- actions -------------------------------------------------------

  const saveMedical = async () => {
    setSavingMedical(true);
    try {
      const saved = await saveMedicalInfo(clientId, medicalDraft);
      setRecord({ ...record, medicalInfo: saved });
      setEditingMedical(false);
      toast.success(`${LABELS.medicalInfo} saved.`, { position: "top-right" });
    } catch (err) {
      toast.error(errorText(err, "Could not save."), { position: "top-right" });
    }
    setSavingMedical(false);
  };

  const saveNote = async () => {
    if (!noteDraft.trim()) return;
    setSavingNote(true);
    try {
      if (editingNote) {
        await updateClientNote(clientId, editingNote.id, noteDraft);
      } else {
        await addClientNote(clientId, {
          body: noteDraft,
          appointmentId: noteFor && noteFor !== "general" ? noteFor : null,
        });
      }
      setNoteFor(null);
      setEditingNote(null);
      setNoteDraft("");
      await load();
    } catch (err) {
      toast.error(errorText(err, "Could not save the note."), { position: "top-right" });
    }
    setSavingNote(false);
  };

  const removeNote = (note: ClientNote) =>
    setConfirming({
      title: "Delete this note?",
      message: "The note is removed from the file. This cannot be undone.",
      confirmText: "Delete note",
      run: () => deleteNote(note),
    });

  const deleteNote = async (note: ClientNote) => {
    try {
      await deleteClientNote(clientId, note.id);
      await load();
    } catch (err) {
      toast.error(errorText(err, "Could not delete the note."), { position: "top-right" });
    }
  };

  const removeAttachment = (attachment: ClientAttachment) =>
    setConfirming({
      title: "Delete this file?",
      message: `"${attachment.title || attachment.fileName}" is removed from the file. This cannot be undone.`,
      confirmText: "Delete file",
      run: () => deleteAttachment(attachment),
    });

  const deleteAttachment = async (attachment: ClientAttachment) => {
    try {
      await deleteClientAttachment(clientId, attachment.id);
      await load();
    } catch (err) {
      toast.error(errorText(err, "Could not delete the file."), { position: "top-right" });
    }
  };

  // Opens the viewer on this page, stepping through `among`.
  const openAttachment = (attachment: ClientAttachment, among: ClientAttachment[] = record.attachments) =>
    setViewer({ files: among, index: Math.max(0, among.findIndex((f) => f.id === attachment.id)) });

  const withdraw = (consent: SignedConsent) =>
    setConfirming({
      title: "Mark as withdrawn?",
      message: `"${consent.title}" will show as withdrawn from today. The signed copy is kept in the file.`,
      confirmText: "Mark as withdrawn",
      run: () => markWithdrawn(consent),
    });

  const markWithdrawn = async (consent: SignedConsent) => {
    try {
      await revokeConsent(clientId, consent.id);
      setViewingConsent(null);
      await load();
    } catch (err) {
      toast.error(errorText(err, "Could not update the consent."), { position: "top-right" });
    }
  };

  // ----- pieces --------------------------------------------------------

  const noteEditor = (
    <Box sx={{ mt: 1 }}>
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={2}
        size="small"
        placeholder={editingNote ? "Edit note" : `Note for this ${LABELS.visit.toLowerCase()}`}
        value={noteDraft}
        onChange={(e) => setNoteDraft(e.target.value)}
        inputProps={{ maxLength: 10000 }}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
        <Button
          size="small"
          onClick={() => {
            setNoteFor(null);
            setEditingNote(null);
            setNoteDraft("");
          }}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={!noteDraft.trim() || savingNote}
          onClick={saveNote}
          sx={{ textTransform: "none" }}
        >
          Save note
        </Button>
      </Box>
    </Box>
  );

  const noteList = (notes: ClientNote[]) =>
    notes.map((note) =>
      editingNote?.id === note.id ? (
        <Box key={note.id}>{noteEditor}</Box>
      ) : (
        <Box
          key={note.id}
          sx={{ mt: 1, p: 1.25, borderRadius: 2, bgcolor: "#F6F8FA", display: "flex", gap: 1, alignItems: "flex-start" }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.88rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {note.body}
            </Typography>
            <Typography sx={{ fontSize: "0.74rem", color: "text.secondary", mt: 0.5 }}>
              {note.createdByName ? `${note.createdByName} · ` : ""}
              {format(new Date(note.createdAt), "d MMM yyyy, HH:mm")}
            </Typography>
          </Box>
          <Tooltip title="Edit note">
            <IconButton
              size="small"
              aria-label="Edit note"
              onClick={() => {
                setNoteFor(null);
                setEditingNote(note);
                setNoteDraft(note.body);
              }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete note">
            <IconButton size="small" aria-label="Delete note" onClick={() => removeNote(note)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    );

  const visitRow = (visit: Visit, withNotes: boolean) => {
    const status = STATUS_STYLE[visit.status] ?? STATUS_STYLE.PENDING;
    const visitNotes = notesByVisit.get(visit.id) ?? [];
    const visitFiles = record.attachments.filter((a) => a.appointmentId === visit.id);
    const start = new Date(visit.startAt);
    const isOpen = openVisit === visit.id || noteFor === visit.id;
    const toggle = () => setOpenVisit(isOpen ? null : visit.id);
    return (
      <Box key={visit.id}>
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          onClick={toggle}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggle())}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 1.1,
            px: 1,
            mx: -1,
            borderRadius: 2,
            cursor: "pointer",
            "&:hover": { bgcolor: "#F6F8FA" },
            "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
          }}
        >
          <Box
            sx={{
              width: 46,
              flexShrink: 0,
              textAlign: "center",
              borderRadius: 2,
              py: 0.5,
              bgcolor: visit.status === "CANCELLED" ? "#F3F4F6" : alpha(theme.palette.primary.main, 0.08),
              color: visit.status === "CANCELLED" ? "text.secondary" : "primary.main",
            }}
          >
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", lineHeight: 1.2 }}>
              {format(start, "MMM")}
            </Typography>
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, lineHeight: 1.1 }}>{format(start, "d")}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }} noWrap>
              {visitDay(visit.startAt)}
              {visit.service?.name ? ` · ${visit.service.name}` : ""}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }} noWrap>
              {[visit.unit?.name, format(start, "yyyy") !== format(new Date(), "yyyy") ? format(start, "yyyy") : null]
                .filter(Boolean)
                .join(" · ") || "—"}
            </Typography>
          </Box>
          {visitNotes.length > 0 && (
            <Tooltip title={`${visitNotes.length} note${visitNotes.length > 1 ? "s" : ""}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: "text.secondary", fontSize: "0.8rem" }}>
                <StickyNote2OutlinedIcon sx={{ fontSize: 17 }} aria-hidden />
                {visitNotes.length}
              </Box>
            </Tooltip>
          )}
          {visitFiles.length > 0 && (
            <Tooltip title={`${visitFiles.length} file${visitFiles.length > 1 ? "s" : ""}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: "text.secondary", fontSize: "0.8rem" }}>
                <AttachFileIcon sx={{ fontSize: 17 }} aria-hidden />
                {visitFiles.length}
              </Box>
            </Tooltip>
          )}
          <Chip size="small" label={status.label} color={status.color} variant="outlined" sx={{ display: { xs: "none", sm: "inline-flex" } }} />
          <ExpandMoreIcon
            aria-hidden
            sx={{ color: "text.secondary", transition: "transform 150ms", transform: isOpen ? "rotate(180deg)" : "none" }}
          />
        </Box>

        <Collapse in={isOpen} unmountOnExit>
          <Box sx={{ pl: { xs: 0, sm: 7.25 }, pb: 1.5 }}>
            <Chip size="small" label={status.label} color={status.color} variant="outlined" sx={{ display: { xs: "inline-flex", sm: "none" }, mb: 1 }} />
            {visit.bookingNote && (
              <Typography sx={{ fontSize: "0.84rem", color: "text.secondary", mb: 0.5 }}>
                Booking note: “{visit.bookingNote}”
              </Typography>
            )}
            {withNotes && noteList(visitNotes)}
            {visitFiles.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {visitFiles.map((file) => (
                  <Chip
                    key={file.id}
                    size="small"
                    variant="outlined"
                    icon={<AttachFileIcon />}
                    label={file.title || file.fileName}
                    onClick={() => openAttachment(file, visitFiles)}
                  />
                ))}
              </Box>
            )}
            {noteFor === visit.id ? (
              noteEditor
            ) : (
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                {withNotes && (
                  <Button
                    size="small"
                    startIcon={<NoteAddOutlinedIcon />}
                    onClick={() => {
                      setEditingNote(null);
                      setNoteDraft("");
                      setNoteFor(visit.id);
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Add note
                  </Button>
                )}
                <Button size="small" startIcon={<UploadFileIcon />} onClick={() => setUploadFor({ visitId: visit.id })} sx={{ textTransform: "none" }}>
                  Add file
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const consentRows = (() => {
    const latestByForm = new Map<string, SignedConsent>();
    for (const consent of record.consents) {
      if (consent.consentFormId && !latestByForm.has(consent.consentFormId)) {
        latestByForm.set(consent.consentFormId, consent);
      }
    }
    // Active forms first (signed or not), then signed copies of forms since removed.
    const rows: { key: string; title: string; form?: ConsentForm; consent?: SignedConsent }[] = forms.map((form) => ({
      key: form.id,
      title: form.title,
      form,
      consent: latestByForm.get(form.id),
    }));
    for (const consent of record.consents) {
      const shown = rows.some((row) => row.consent?.id === consent.id);
      if (!shown && !forms.some((f) => f.id === consent.consentFormId)) {
        rows.push({ key: consent.id, title: consent.title, consent });
      }
    }
    return rows;
  })();

  const generalNotes = notesByVisit.get("general") ?? [];
  const shownTab = visitTab ?? (record.upcomingVisits.length > 0 ? "upcoming" : "past");
  const visitsById = new Map([...record.upcomingVisits, ...record.pastVisits].map((v) => [v.id, v]));
  // Types present on this client's files, with how many each.
  const fileTypes = Array.from(
    record.attachments.reduce((map, f) => map.set(f.category, (map.get(f.category) ?? 0) + 1), new Map<string, number>())
  );
  const shownFiles = fileFilter ? record.attachments.filter((f) => f.category === fileFilter) : record.attachments;

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {/* Medical / health info: always first, allergies in red. */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: hasAllergies ? "#F0C2BD" : "divider",
          bgcolor: hasAllergies ? "#FDF3F2" : "background.paper",
          borderRadius: 3,
          p: { xs: 1.5, sm: 2 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          {hasAllergies && <WarningAmberIcon sx={{ color: "#A32B22" }} aria-hidden />}
          <Typography component="h3" sx={{ fontWeight: 700, fontSize: "1rem", flexGrow: 1 }}>
            {LABELS.medicalInfo}
          </Typography>
          <Button
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={() => {
              setMedicalDraft(toMedicalValues(info));
              setEditingMedical(true);
            }}
            sx={{ textTransform: "none" }}
          >
            {info ? "Edit" : "Add"}
          </Button>
        </Box>
        {!info ? (
          <Empty>Nothing recorded yet: allergies, conditions, medications, emergency contact.</Empty>
        ) : (
          <Box sx={{ display: "grid", gap: 1.25 }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#A32B22", minWidth: 86 }}>
                Allergies
              </Typography>
              {hasAllergies ? (
                info.allergies.map((a) => <Chip key={a} size="small" color="error" label={a} />)
              ) : (
                <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>None known</Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "text.secondary", minWidth: 86 }}>
                Conditions
              </Typography>
              {info.conditions.length ? (
                info.conditions.map((c) => <Chip key={c} size="small" variant="outlined" label={c} />)
              ) : (
                <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>None recorded</Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "grid",
                gap: 1,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                fontSize: "0.85rem",
              }}
            >
              <Typography sx={{ fontSize: "0.85rem" }}>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  Medications:{" "}
                </Box>
                {info.medications || "—"}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem" }}>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  Blood type:{" "}
                </Box>
                {info.bloodType || "—"}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem" }}>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  Emergency:{" "}
                </Box>
                {info.emergencyContactName || info.emergencyContactPhone
                  ? [info.emergencyContactName, info.emergencyContactPhone].filter(Boolean).join(" · ")
                  : "—"}
              </Typography>
            </Box>
            {info.notes && (
              <Typography sx={{ fontSize: "0.85rem", whiteSpace: "pre-wrap" }}>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  Notes:{" "}
                </Box>
                {info.notes}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" }, alignItems: "start" }}>
        <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
          <Section
            title={LABELS.visits}
            action={
              <ToggleButtonGroup
                size="small"
                exclusive
                value={shownTab}
                onChange={(_, value) => value && (setVisitTab(value), setOpenVisit(null))}
                aria-label={`Which ${LABELS.visits.toLowerCase()}`}
                sx={{ "& .MuiToggleButton-root": { textTransform: "none", py: 0.25, px: 1.25, fontSize: "0.8rem" } }}
              >
                <ToggleButton value="upcoming">Upcoming · {record.upcomingVisits.length}</ToggleButton>
                <ToggleButton value="past">History · {record.pastVisits.length}</ToggleButton>
              </ToggleButtonGroup>
            }
          >
            {shownTab === "upcoming" ? (
              record.upcomingVisits.length === 0 ? (
                <Empty>Nothing booked.</Empty>
              ) : (
                record.upcomingVisits.map((visit, i) => (
                  <Box key={visit.id}>
                    {i > 0 && <Divider />}
                    {visitRow(visit, false)}
                  </Box>
                ))
              )
            ) : record.pastVisits.length === 0 ? (
              <Empty>No past {LABELS.visits.toLowerCase()} yet.</Empty>
            ) : (
              <>
                {record.pastVisits.slice(0, historyShown).map((visit, i) => (
                  <Box key={visit.id}>
                    {i > 0 && <Divider />}
                    {visitRow(visit, true)}
                  </Box>
                ))}
                {record.pastVisits.length > historyShown && (
                  <Button
                    size="small"
                    onClick={() => setHistoryShown((n) => n + HISTORY_PAGE * 2)}
                    sx={{ textTransform: "none", mt: 0.5 }}
                  >
                    Show more ({record.pastVisits.length - historyShown} older)
                  </Button>
                )}
              </>
            )}
          </Section>
        </Box>

        <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
          <Section title="Consent forms">
            {consentRows.length === 0 ? (
              <Empty>No consent forms set up yet. The owner adds them in {LABELS.clientFile} settings.</Empty>
            ) : (
              consentRows.map((row, i) => {
                const signed = row.consent && !row.consent.revokedAt;
                return (
                  <Box key={row.key}>
                    {i > 0 && <Divider />}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.1 }}>
                      <DrawOutlinedIcon sx={{ fontSize: 18, color: signed ? "#16A34A" : "text.secondary" }} aria-hidden />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }} noWrap>
                          {row.title}
                        </Typography>
                        <Typography sx={{ fontSize: "0.76rem", color: "text.secondary" }}>
                          {row.consent
                            ? row.consent.revokedAt
                              ? `Withdrawn ${format(new Date(row.consent.revokedAt), "d MMM yyyy")}`
                              : `Signed ${format(new Date(row.consent.signedAt), "d MMM yyyy")} by ${row.consent.signedName}`
                            : "Not signed"}
                        </Typography>
                      </Box>
                      {row.consent && (
                        <Button size="small" onClick={() => setViewingConsent(row.consent!)} sx={{ textTransform: "none" }}>
                          View
                        </Button>
                      )}
                      {row.form && !signed && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSigning(row.form!)}
                          sx={{ textTransform: "none" }}
                        >
                          Sign
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Section>

          <Section
            title="Files"
            action={
              <Button size="small" startIcon={<UploadFileIcon />} onClick={() => setUploadFor({})} sx={{ textTransform: "none" }}>
                Add file
              </Button>
            }
          >
            {record.attachments.length === 0 ? (
              <Empty>No files yet: lab results, X-rays, reports.</Empty>
            ) : (
              <>
                {fileTypes.length > 1 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                    <Chip size="small" label={`All · ${record.attachments.length}`} color={fileFilter ? "default" : "primary"} variant={fileFilter ? "outlined" : "filled"} onClick={() => setFileFilter(null)} />
                    {fileTypes.map(([name, count]) => (
                      <Chip
                        key={name}
                        size="small"
                        label={`${name} · ${count}`}
                        color={fileFilter === name ? "primary" : "default"}
                        variant={fileFilter === name ? "filled" : "outlined"}
                        onClick={() => setFileFilter(fileFilter === name ? null : name)}
                      />
                    ))}
                  </Box>
                )}
                {shownFiles.map((file, i) => {
                  const visit = file.appointmentId ? visitsById.get(file.appointmentId) : undefined;
                  return (
                    <Box key={file.id}>
                      {i > 0 && <Divider />}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1 }}>
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => openAttachment(file, shownFiles)}
                          onKeyDown={(e) => e.key === "Enter" && openAttachment(file, shownFiles)}
                          sx={{ display: "flex", alignItems: "center", gap: 1.25, flexGrow: 1, minWidth: 0, cursor: "pointer", borderRadius: 2, "&:hover .file-title": { textDecoration: "underline" } }}
                        >
                          <FileTypeIcon file={file} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography className="file-title" sx={{ fontWeight: 600, fontSize: "0.88rem", color: "primary.main" }} noWrap>
                              {file.title || file.fileName}
                            </Typography>
                            <Typography sx={{ fontSize: "0.74rem", color: "text.secondary" }} noWrap>
                              {[
                                file.category,
                                format(new Date(file.createdAt), "d MMM yyyy"),
                                fileSize(file.sizeBytes),
                                visit ? `${LABELS.visit} ${format(new Date(visit.startAt), "d MMM")}` : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </Typography>
                          </Box>
                        </Box>
                        <Tooltip title="Delete file">
                          <IconButton size="small" aria-label="Delete file" onClick={() => removeAttachment(file)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </Section>

          <Section
            title="General notes"
            action={
              noteFor !== "general" && (
                <Button
                  size="small"
                  startIcon={<NoteAddOutlinedIcon />}
                  onClick={() => {
                    setEditingNote(null);
                    setNoteDraft("");
                    setNoteFor("general");
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Add note
                </Button>
              )
            }
          >
            {noteFor === "general" && noteEditor}
            {generalNotes.length === 0 && noteFor !== "general" ? (
              <Empty>Notes not tied to a {LABELS.visit.toLowerCase()}.</Empty>
            ) : (
              noteList(generalNotes)
            )}
          </Section>
        </Box>
      </Box>

      {/* Edit medical / health info */}
      <Dialog open={editingMedical} onClose={() => !savingMedical && setEditingMedical(false)} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{LABELS.medicalInfo}</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <MedicalInfoFields value={medicalDraft} onChange={setMedicalDraft} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditingMedical(false)} disabled={savingMedical} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation onClick={saveMedical} disabled={savingMedical} sx={{ textTransform: "none" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sign a consent form */}
      <Dialog open={Boolean(signing)} onClose={() => setSigning(null)} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Sign consent</DialogTitle>
        <DialogContent>
          {signing && (
            <ConsentSignPanel
              clientId={clientId}
              form={signing}
              defaultName={record.client.fullName}
              onCancel={() => setSigning(null)}
              onSigned={async () => {
                setSigning(null);
                toast.success("Consent signed.", { position: "top-right" });
                await load();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View a signed consent */}
      <Dialog open={Boolean(viewingConsent)} onClose={() => setViewingConsent(null)} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{viewingConsent?.title}</DialogTitle>
        <DialogContent>
          {viewingConsent && (
            <>
              {viewingConsent.revokedAt && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Withdrawn on {format(new Date(viewingConsent.revokedAt), "d MMM yyyy")}.
                </Alert>
              )}
              <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "0.9rem", lineHeight: 1.6, mb: 2 }}>
                {viewingConsent.body}
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {viewingConsent.signature && (
                <Box
                  component="img"
                  src={viewingConsent.signature}
                  alt={`Signature of ${viewingConsent.signedName}`}
                  sx={{ maxWidth: "100%", maxHeight: 140, display: "block", mb: 1 }}
                />
              )}
              <Typography sx={{ fontSize: "0.85rem" }}>
                Signed by <strong>{viewingConsent.signedName}</strong> on{" "}
                {format(new Date(viewingConsent.signedAt), "d MMM yyyy, HH:mm")}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {viewingConsent && !viewingConsent.revokedAt && (
            <Button color="error" onClick={() => withdraw(viewingConsent)} sx={{ textTransform: "none", mr: "auto" }}>
              Mark as withdrawn
            </Button>
          )}
          <Button onClick={() => setViewingConsent(null)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <UploadFileDialog
        open={Boolean(uploadFor)}
        clientId={clientId}
        visits={[...record.upcomingVisits, ...record.pastVisits]}
        visitId={uploadFor?.visitId}
        onClose={() => setUploadFor(null)}
        onUploaded={async () => {
          setUploadFor(null);
          toast.success("File added.", { position: "top-right" });
          await load();
        }}
      />

      <ConfirmDialog
        open={Boolean(confirming)}
        title={confirming?.title}
        message={confirming?.message}
        confirmButtonText={confirming?.confirmText}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          const action = confirming;
          setConfirming(null);
          action?.run();
        }}
      />

      <AttachmentViewer
        clientId={clientId}
        files={viewer.files}
        index={viewer.index}
        onIndexChange={(index) => setViewer((v) => ({ ...v, index }))}
      />
    </Box>
  );
};

export default ClientFile;
